import React from 'react';

import { Trans, useTranslation } from 'react-i18next';

import { FileUploadDragAndDrop } from 'components/shared/File/FileUploadDragAndDrop';
import { isValidFileSize, isValidImageType } from 'shared/file-utils';
import { toast } from 'shared/toast';

import './styles.scss';
import { optimizePhoto } from 'shared/canvas-utils/canvas-utils';

interface IImageUploaderProps {
    setImage: React.Dispatch<React.SetStateAction<string | undefined>>;
}

export const ImageUploader = ({setImage}: IImageUploaderProps) => {
    const {t} = useTranslation();

    /**
     * Checks if the uploaded image is valid. Checks file size, file type, and if the file is a duplicate.
     * displays a toast message explaining why the upload failed.
     *
     * @param image - object: image
     *
     * @returns boolean - true if the image is valid, false if not
     */
    const isValidDocumentUpload = (image: File): boolean => {
        if (!(image instanceof File)) return false;
        if (!isValidImageType(image)) {
            toast.warning(
                t('common.imageUploadFailed', 'Image Upload Failed'),
                t('common.imageTypeNotSupported', 'Image type not supported')
            );
            return false;
        } else if (!isValidFileSize(image, 5)) {
            toast.warning(
                t('common.imageUploadFailed', 'Image Upload Failed'),
                t('common.fileIsTooLarge', 'File size is too large')
            );
            return false;
        }
        return true;
    };

    // /**
    //  * Creates an image data url from a file
    //  *
    //  * @param file - uploaded file
    //  */
    // const readFile = (file: File): Promise<string | ArrayBuffer | null> => {
    //     return new Promise((resolve) => {
    //         const reader = new FileReader();
    //         reader.addEventListener('load', () => resolve(reader.result), false);
    //         reader.readAsDataURL(file);
    //     });
    // };

    /**
     * Checks the image for validity, both for an invalid image and one with a high enough resolution
     * Sets the image state if acceptable
     *
     * @param file - uploaded file
     * @param imageDataUrl - image data URL
     */
    const handleSetImage = (file: File, imageDataUrl: string) => {
        const image = new Image();
        image.src = window.URL.createObjectURL(file);
        image.onload = function () {
            // Check if image is bad/invalid
            if (image.naturalWidth + image.naturalHeight === 0) {
                toast.warning(
                    t('common.imageUploadFailed', 'Image Upload Failed'),
                    t('common.imageInvalid', 'Image is Invalid')
                );
            }
            // Check the image resolution
            if (image.naturalWidth >= 400 && image.naturalHeight >= 400) {
                setImage(imageDataUrl);
            } else {
                toast.warning(
                    t('common.imageUploadFailed', 'Image Upload Failed'),
                    t('common.imageResolutionTooLow', 'Image Resolution must be at least 400 x 400px')
                );
            }
        };
    };

    /**
     * Controls file handling if files are dropped in the field. Multiple files can be dropped but one one will be read
     *
     * @param files - list of files dropped
     */
    const onDrop = async (files: FileList | null) => {
        if (files) {
            const file = files.item(0);
            if (file && await isValidDocumentUpload(file)) {
                // const imageDataUr = await readFile(file);
                const imageDataUrl = await optimizePhoto(file);
                handleSetImage(file, imageDataUrl);
            }
        }
    };

    /**
     * If a file is selected using the browser this grabs the files from the event and sends it to onDrop for validating
     *
     * @param event - file selection event
     */
    const onFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        const {files} = event.target;
        await onDrop(files);
    };

    return (
        <>
            <div className="UpdateProfileImage__title">
                {t('UpdateProfileImage.uploadAFile', 'Upload a File')}
            </div>
            <div className="UpdateProfileImage__subtitle mb-3">
                <Trans i18nKey="UpdateProfileImage.pleaseUploadImage">
                    Please upload a JPEG or PNG image at least 400px wide and 400px high. The profile image must be
                    a recent and genuine, high-quality passport style image of your face.
                </Trans>
            </div>
            <FileUploadDragAndDrop
                className="text-center bg-white"
                onDrop={onDrop}
                onFileInputChange={onFileInputChange}
            >
                <div className="font-weight-bold">
                    {t('actions.dropFilesHere', 'Drop Files Here')}
                </div>
                <div>{t('actions.dropFilesHereSubtitle', 'Or click to browse your computer')}</div>
            </FileUploadDragAndDrop>
        </>
    );
};
